import { motion } from 'framer-motion';
import { Download, Leaf, Sparkles, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BRAND } from '@/lib/brand';

export const ProductHero = ({ product, onDownload }) => (
  <section className="relative py-10 md:py-16">
    <div className="container mx-auto grid lg:grid-cols-12 gap-10 items-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="lg:col-span-6 order-2 lg:order-1"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/70 ring-leaf text-xs font-medium text-primary">
          <Leaf className="h-3.5 w-3.5 text-leaf" />
          {product.category} · {product.tagline}
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary font-medium leading-[1.02] mt-5 text-balance">
          {product.name.split(' ').slice(0, -1).join(' ')}{' '}
          <span className="italic font-light text-leaf">{product.name.split(' ').slice(-1)}</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
          {product.long}
        </p>

        <div className="mt-7 flex flex-wrap gap-2">
          {product.certs.map((c) => (
            <Badge key={c} variant="outline" className="border-leaf/30 text-leaf bg-leaf/5 rounded-full px-3 py-1">
              <ShieldCheck className="h-3 w-3 mr-1" /> {c}
            </Badge>
          ))}
        </div>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Button onClick={onDownload} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-7 h-12">
            <Download className="mr-1.5 h-4 w-4" /> Download sell sheet (PDF)
          </Button>
          <Button asChild variant="outline" className="rounded-full border-primary/25 text-primary hover:bg-secondary px-6 h-12">
            <a href={`mailto:${BRAND.email}?subject=Sample request: ${product.name}`}>Request samples</a>
          </Button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-6 order-1 lg:order-2 relative"
      >
        <div className="relative aspect-square rounded-[2.5rem] overflow-hidden shadow-pop bg-secondary">
          <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
          <div className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-background/85 backdrop-blur text-xs font-medium text-primary tracking-wide">
            {product.unitCase} · {product.weight}
          </div>
          <div className="absolute right-5 -bottom-5 h-28 w-28 rounded-full bg-accent text-accent-foreground grid place-items-center shadow-amber rotate-[-6deg]">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.18em]">Format</div>
              <div className="font-display text-[11px] font-semibold mt-1 leading-tight px-2">{product.format.split('·')[0].trim()}</div>
              <Sparkles className="h-3.5 w-3.5 mx-auto mt-1" />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default ProductHero;
